import { useState, useEffect } from 'react';
import { shippingApi } from '../services/api';

interface UseShippingRecordLinksProps {
  pendingRecordId: number | null;
  isEditMode: boolean;
  numericId?: number;
}

export const useShippingRecordLinks = ({ pendingRecordId, isEditMode, numericId }: UseShippingRecordLinksProps) => {
  const [currentlyLinkedRepairRecords, setCurrentlyLinkedRepairRecords] = useState<any[]>([]);
  const [currentlyLinkedFuelReceipts, setCurrentlyLinkedFuelReceipts] = useState<any[]>([]);
  const [currentlyLinkedPrepaidRecords, setCurrentlyLinkedPrepaidRecords] = useState<any[]>([]);
  const [availableUnlinkedRepairRecords, setAvailableUnlinkedRepairRecords] = useState<any[]>([]);
  const [availableUnlinkedFuelReceipts, setAvailableUnlinkedFuelReceipts] = useState<any[]>([]);
  const [availableUnlinkedPrepaidRecords, setAvailableUnlinkedPrepaidRecords] = useState<any[]>([]);

  const fetchLinkedReceipts = async (shippingRecordId: number) => {
    console.log('fetchLinkedReceipts called with ID:', shippingRecordId);
    try {
      const response = await shippingApi.getLinkedReceipts(shippingRecordId);
      console.log('fetchLinkedReceipts response:', response.data);
      setCurrentlyLinkedRepairRecords(response.data.repairRecords || []);
      setCurrentlyLinkedFuelReceipts(response.data.fuelReceipts || []);
      setCurrentlyLinkedPrepaidRecords(response.data.prepaidRecords || []);
    } catch (error) {
      console.error('Error fetching linked receipts:', error);
    }
  };

  const fetchAvailableReceipts = async () => {
    try {
      const [repairResponse, fuelResponse, prepaidResponse] = await Promise.all([
        shippingApi.getAvailableRepairRecords(),
        shippingApi.getAvailableFuelReceipts(),
        shippingApi.getAvailablePrepaidRecords()
      ]);
      setAvailableUnlinkedRepairRecords(repairResponse.data || []);
      setAvailableUnlinkedFuelReceipts(fuelResponse.data || []);
      setAvailableUnlinkedPrepaidRecords(prepaidResponse.data || []);
    } catch (error) {
      console.error('Error fetching available receipts:', error);
    }
  };

  const handleLinkRepairRecord = async (repairRecordId: number) => {
    if (!pendingRecordId) return;
    try {
      await shippingApi.linkRepairRecord(pendingRecordId, repairRecordId);
      await fetchLinkedReceipts(pendingRecordId);
      await fetchAvailableReceipts();
    } catch (error) {
      console.error('Error linking repair record:', error);
      alert('Error linking repair record');
    }
  };

  const handleLinkFuelReceipt = async (fuelReceiptId: number) => {
    console.log('handleLinkFuelReceipt called', { fuelReceiptId, pendingRecordId });
    if (!pendingRecordId) {
      console.error('No pending record ID available');
      alert('No pending record ID available');
      return;
    }
    try {
      await shippingApi.linkFuelReceipt(pendingRecordId, fuelReceiptId);
      await fetchLinkedReceipts(pendingRecordId);
      await fetchAvailableReceipts();
    } catch (error) {
      console.error('Error linking fuel receipt:', error);
      alert('Error linking fuel receipt');
    }
  };

  const handleUnlinkRepairRecord = async (repairRecordId: number) => {
    if (!pendingRecordId) return;
    try {
      await shippingApi.unlinkRepairRecord(pendingRecordId, repairRecordId);
      await fetchLinkedReceipts(pendingRecordId);
      await fetchAvailableReceipts();
    } catch (error) {
      console.error('Error unlinking repair record:', error);
      alert('Error unlinking repair record');
    }
  };

  const handleUnlinkFuelReceipt = async (fuelReceiptId: number) => {
    if (!pendingRecordId) return;
    try {
      await shippingApi.unlinkFuelReceipt(pendingRecordId, fuelReceiptId);
      await fetchLinkedReceipts(pendingRecordId);
      await fetchAvailableReceipts();
    } catch (error) {
      console.error('Error unlinking fuel receipt:', error);
      alert('Error unlinking fuel receipt');
    }
  };

  const handleLinkPrepaidRecord = async (prepaidRecordId: number) => {
    if (!pendingRecordId) return;
    try {
      await shippingApi.linkPrepaidRecord(pendingRecordId, prepaidRecordId);
      await fetchLinkedReceipts(pendingRecordId);
      await fetchAvailableReceipts();
    } catch (error) {
      console.error('Error linking prepaid record:', error);
      alert('Error linking prepaid record');
    }
  };

  const handleUnlinkPrepaidRecord = async (prepaidRecordId: number) => {
    if (!pendingRecordId) return;
    try {
      await shippingApi.unlinkPrepaidRecord(pendingRecordId, prepaidRecordId);
      await fetchLinkedReceipts(pendingRecordId);
      await fetchAvailableReceipts();
    } catch (error) {
      console.error('Error unlinking prepaid record:', error);
      alert('Error unlinking prepaid record');
    }
  };

  // Fetch linked and available receipts when pendingRecordId or isEditMode changes
  useEffect(() => {
    if (pendingRecordId || (isEditMode && numericId)) {
      const recordId = pendingRecordId || numericId!;
      fetchLinkedReceipts(recordId);
    }
    fetchAvailableReceipts();
  }, [pendingRecordId, isEditMode, numericId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    currentlyLinkedRepairRecords,
    currentlyLinkedFuelReceipts,
    currentlyLinkedPrepaidRecords,
    availableUnlinkedRepairRecords,
    availableUnlinkedFuelReceipts,
    availableUnlinkedPrepaidRecords,
    handleLinkRepairRecord,
    handleLinkFuelReceipt,
    handleUnlinkRepairRecord,
    handleUnlinkFuelReceipt,
    handleLinkPrepaidRecord,
    handleUnlinkPrepaidRecord
  };
};
